import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { intlShape, injectIntl, FormattedMessage } from 'react-intl';
import { WalkthroughPopover } from '../../common/components';
import { Input, Button, Icon, Tooltip } from '@salesforce/design-system-react';

import * as Constants from '../constants';
import { sendEvent } from '../../utilities/instrumentation';

import './FrequencyCap.scss';

export class FrequencyCap extends Component {
  constructor(props) {
    super(props);
    this.state = {
      oversaturated: '',
      undersaturated: ''
    };
  }

  componentDidMount() {
    const { oversaturated, undersaturated } = this.props;
    if (oversaturated) {
      this.setState({
        oversaturated: oversaturated.toString()
      });
    }
    if (undersaturated) {
      this.setState({
        undersaturated: undersaturated.toString()
      });
    }
  }

  componentDidUpdate(prevProps) {
    const { oversaturated, undersaturated } = this.props;
    if (
      prevProps.oversaturated !== oversaturated ||
      prevProps.undersaturated !== undersaturated
    ) {
      this.setState({
        oversaturated: oversaturated ? oversaturated.toString() : '',
        undersaturated: undersaturated ? undersaturated.toString() : ''
      });
    }
  }

  handleUpdateInput(name, event) {
    let newState = {};
    newState[name] = event.target.value;
    this.setState(newState);
  }

  handleUpdateDataFilter(name, event) {
    this.props.actions.updateDataFilter({
      [name]: Number(event.target.value)
    });
  }

  handleAdjustFrequencyThreshold() {
    this.props.actions.updateDataFilter({
      undersaturated: this.props.einsteinFrequencyRecs.undersaturated,
      oversaturated: this.props.einsteinFrequencyRecs.oversaturated
    });
    sendEvent('clickButton', 'applyFrequencyRecommendation');
  }

  renderErrorMessage() {
    const { formatMessage } = this.props.intl;
    const { rangeStart, rangeEnd } = this.props.dataExtension.chartParams;
    const { undersaturated, oversaturated } = this.props;
    const { status } = this.props.einsteinFrequencyRecs;
    const rangeStartError = rangeStart !== null && rangeStart > undersaturated;
    const rangeEndError = rangeEnd !== null && rangeEnd < oversaturated;
    const rangeError = rangeStartError || rangeEndError;
    const errorStatus = rangeError ? 'out_of_range' : status;
    const errorText =
      errorStatus && errorStatus !== Constants.RECOMMENDATION_STATUS.ok
        ? formatMessage({ id: `FrequencyCap.error.${errorStatus}` })
        : null;
    return errorStatus !== Constants.RECOMMENDATION_STATUS.ok ? (
      <div className="error-message slds-grid">
        <div className="error-icon slds-col">
          <Icon
            assistiveText={{ label: 'More Information' }}
            category="utility"
            name="info"
            size="x-small"
          />
        </div>
        <div className="slds-col">
          <p>{errorText}</p>
        </div>
      </div>
    ) : null;
  }

  render() {
    const { formatMessage } = this.props.intl;
    const titleTooltip = (
      <Tooltip
        align="left"
        content={formatMessage({ id: 'FrequencyCap.tooltip' })}
      >
        <button className="slds-button tooltip-trigger-button">
          <Icon
            assistiveText={{ label: 'More Information' }}
            category="utility"
            name="info"
            size="x-small"
          />
        </button>
      </Tooltip>
    );

    const { undersaturated, oversaturated, ftux, actions } = this.props;
    const disableEinsteinButton =
      undersaturated === this.props.einsteinFrequencyRecs.undersaturated &&
      oversaturated === this.props.einsteinFrequencyRecs.oversaturated;

    return (
      <div className="frequency-cap-container">
        <div className="frequency-cap">
          <h1 className="section-title">
            <FormattedMessage
              id="FrequencyCap.title"
              values={{ tooltip: titleTooltip }}
            />
          </h1>
          <div className="slds-grid slds-gutters_direct frequency-range">
            <div className="selection-container slds-col slds-size_1-of-2">
              <h2 className="input-label">
                <FormattedMessage id="FrequencyCap.undersaturated.title" />
              </h2>
              <Input
                id="underSaturated"
                value={this.state.undersaturated}
                onChange={e => this.handleUpdateInput('undersaturated', e)}
                onBlur={e => this.handleUpdateDataFilter('undersaturated', e)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    this.handleUpdateDataFilter('undersaturated', e);
                  }
                }}
              />
            </div>
            <div className="selection-container slds-col slds-size_1-of-2">
              <h2 className="input-label">
                <FormattedMessage id="FrequencyCap.oversaturated.title" />
              </h2>
              <Input
                id="overSaturated"
                value={this.state.oversaturated}
                onChange={e => this.handleUpdateInput('oversaturated', e)}
                onBlur={e => this.handleUpdateDataFilter('oversaturated', e)}
                onKeyPress={e => {
                  if (e.key === 'Enter') {
                    this.handleUpdateDataFilter('oversaturated', e);
                  }
                }}
              />
            </div>
          </div>
          {this.renderErrorMessage()}
          <Button
            iconCategory="utility"
            iconName="einstein"
            iconPosition="left"
            label={formatMessage({ id: 'FrequencyCap.button' })}
            disabled={disableEinsteinButton}
            onClick={e => this.handleAdjustFrequencyThreshold()}
          />
        </div>
        <WalkthroughPopover
          step={ftux.steps.get('preferredFrequencyRange')}
          numSteps={ftux.numSteps}
          position="left"
          actions={actions}
        />
      </div>
    );
  }
}

FrequencyCap.propTypes = {
  children: PropTypes.element,
  location: PropTypes.object,
  undersaturated: PropTypes.number,
  oversaturated: PropTypes.number,
  dataExtension: PropTypes.object.isRequired,
  einsteinFrequencyRecs: PropTypes.object,
  ftux: PropTypes.object,
  actions: PropTypes.object,
  intl: intlShape.isRequired
};

export default injectIntl(FrequencyCap);
